// TraitementFeuillesTempsManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, Filter, Check, X, User, Clock, AlertCircle,
    FileText, ChevronDown, Loader, Calendar, BarChart2,
    TrendingUp, Eye,
} from "lucide-react";
import {
    fetchManagerInboxRequest,
    managerDecisionRequest,
} from "../../../../redux/actions/timesheetWorkflowActions";

import "../TraitementStyles/TraitementFeuillesTemps.css";

const TraitementFeuillesTempsManager = () => {
    const dispatch = useDispatch();

    /* ── Redux ──────────────────────────────────────────────── */
    const { managerInbox = [], loading = false } = useSelector(
        (s) => s.timesheetWorkflow || {}
    );

    /* ── Local state ─────────────────────────────────────────── */
    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");
    const [filters, setFilters] = useState({
        statut: "", dateDebut: "", dateFin: "", tauxMin: "",
    });

    useEffect(() => {
        dispatch(fetchManagerInboxRequest());
    }, [dispatch]);

    /* ── Helpers ─────────────────────────────────────────────── */
    const fmt = (d) => {
        if (!d) return "N/A";
        return new Date(d).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const mapTab = (s) => {
        if (s === "PENDING_MANAGER") return "enCours";
        if (s === "APPROVED_MANAGER" || s === "APPROVED") return "approuve";
        if (s === "REJECTED_MANAGER" || s === "REJECTED") return "rejete";
        return "enCours";
    };

    const statutBadge = (s) => {
        const cfg = {
            PENDING_MANAGER: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            APPROVED_MANAGER: { label: "Validé", bg: "#d4edda", color: "#155724" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED_MANAGER: { label: "Refusé", bg: "#f8d7da", color: "#721c24" },
            REJECTED: { label: "Refusé", bg: "#f8d7da", color: "#721c24" },
        };
        const c = cfg[s] || cfg.PENDING_MANAGER;
        return (
            <span className="tftBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const tauxColor = (t) => {
        if (t >= 90) return "#10b981";
        if (t >= 70) return "#f59e0b";
        return "#ef4444";
    };

    const TauxBar = ({ taux }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 100 }}>
            <div style={{
                flex: 1, height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden",
            }}>
                <div style={{
                    height: "100%", width: `${taux ?? 0}%`,
                    background: tauxColor(taux ?? 0), borderRadius: 3,
                    transition: "width .3s",
                }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: tauxColor(taux ?? 0), minWidth: 32 }}>
                {taux ?? 0}%
            </span>
        </div>
    );

    /* ── Actions ─────────────────────────────────────────────── */
    const handleApprove = (id) => {
        dispatch(managerDecisionRequest({ id, decision: true, comment: "" }));
    };

    const openReject = (item) => { setSelected(item); setShowRejectModal(true); };
    const closeReject = () => { setShowRejectModal(false); setMotifRejet(""); setSelected(null); };
    const confirmReject = () => {
        if (!motifRejet.trim() || !selected) return;
        dispatch(managerDecisionRequest({ id: selected.id, decision: false, comment: motifRejet }));
        closeReject();
    };

    const openDetails = (item) => { setSelected(item); setShowDetailsModal(true); };
    const closeDetails = () => { setShowDetailsModal(false); setSelected(null); };

    /* ── Filtrage ────────────────────────────────────────────── */
    const filtered = useMemo(() => {
        let list = (managerInbox || []).filter((d) => mapTab(d.status) === activeTab);
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (d) =>
                    d.employeeName?.toLowerCase().includes(s) ||
                    d.employeeMatricule?.toLowerCase().includes(s) ||
                    d.periodLabel?.toLowerCase().includes(s)
            );
        }
        if (filters.dateDebut) list = list.filter((d) => d.periodStart >= filters.dateDebut);
        if (filters.dateFin) list = list.filter((d) => d.periodEnd <= filters.dateFin);
        if (filters.tauxMin) list = list.filter((d) => (d.tauxRemplissage ?? 0) >= Number(filters.tauxMin));
        return list;
    }, [managerInbox, activeTab, searchTerm, filters]);

    const tabs = useMemo(() => [
        { id: "enCours", label: "En attente", count: (managerInbox || []).filter(d => d.status === "PENDING_MANAGER").length },
        { id: "approuve", label: "Validés", count: (managerInbox || []).filter(d => d.status === "APPROVED_MANAGER" || d.status === "APPROVED").length },
        { id: "rejete", label: "Refusés", count: (managerInbox || []).filter(d => d.status === "REJECTED_MANAGER" || d.status === "REJECTED").length },
    ], [managerInbox]);

    /* ── Render ──────────────────────────────────────────────── */
    if (loading && !managerInbox?.length) {
        return (
            <div className="tftPage">
                <div className="tftWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tftPage">
            <div className="tftWrap">

                {/* Header */}
                <div className="tftHeader">
                    <div>
                        <h1>Traitement des feuilles de temps</h1>
                        <p>Approuvez les heures travaillées et activités de votre équipe</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tftCard">
                    <div className="tftControls">
                        <div className="tftSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, période…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button type="button" className="tftFilterBtn" onClick={() => setShowFilters(v => !v)}>
                            <Filter size={16} /> Filtres
                            <ChevronDown size={16} className={`tftChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tftFilters">
                            <div className="tftField">
                                <label>Période début</label>
                                <input type="date" value={filters.dateDebut}
                                    onChange={e => setFilters(p => ({ ...p, dateDebut: e.target.value }))} />
                            </div>
                            <div className="tftField">
                                <label>Période fin</label>
                                <input type="date" value={filters.dateFin}
                                    onChange={e => setFilters(p => ({ ...p, dateFin: e.target.value }))} />
                            </div>
                            <div className="tftField">
                                <label>Taux min (%)</label>
                                <input type="number" min={0} max={100} value={filters.tauxMin} placeholder="ex: 80"
                                    onChange={e => setFilters(p => ({ ...p, tauxMin: e.target.value }))} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tftCard tftCard--noPad">
                    <div className="tftTabs">
                        {tabs.map(tab => (
                            <button key={tab.id} type="button"
                                className={`tftTab ${activeTab === tab.id ? "is-active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                                <span className={`tftCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="tftTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tftEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune feuille de temps trouvée</div>
                            </div>
                        ) : (
                            <table className="tftTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Période</th>
                                        <th>Heures saisies</th>
                                        <th>H. supp.</th>
                                        <th>Taux remplissage</th>
                                        <th>Date soumission</th>
                                        <th>Statut</th>
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((d) => (
                                        <tr key={d.id} onClick={() => openDetails(d)} style={{ cursor: "pointer" }}>
                                            <td><span className="tftPrimary">{d.employeeMatricule || "—"}</span></td>
                                            <td>
                                                <div className="tftName">
                                                    <span className="tftPrimary">{d.employeeName}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <Calendar size={14} style={{ color: "#6b7280", flexShrink: 0 }} />
                                                    <div className="tftPeriod">
                                                        <div>{fmt(d.periodStart)}</div>
                                                        <div>→ {fmt(d.periodEnd)}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                    <Clock size={14} style={{ color: "#6b7280" }} />
                                                    <span className="tftPrimary">{d.totalHeures ?? 0}h</span>
                                                </div>
                                            </td>
                                            <td>
                                                {(d.heuresSupp ?? 0) > 0 ? (
                                                    <span style={{ color: "#f59e0b", fontWeight: 600 }}>
                                                        +{d.heuresSupp}h
                                                    </span>
                                                ) : <span className="tftMuted">—</span>}
                                            </td>
                                            <td><TauxBar taux={d.tauxRemplissage} /></td>
                                            <td><span className="tftMuted">{fmt(d.dateEnvoi)}</span></td>
                                            <td>{statutBadge(d.status)}</td>
                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                {d.status === "PENDING_MANAGER" ? (
                                                    <div className="tftRowActions">
                                                        <button type="button" className="tftActionBtn tftActionBtn--ok"
                                                            onClick={() => handleApprove(d.id)} title="Approuver" disabled={loading}>
                                                            <Check size={16} />
                                                        </button>
                                                        <button type="button" className="tftActionBtn tftActionBtn--no"
                                                            onClick={() => openReject(d)} title="Refuser" disabled={loading}>
                                                            <X size={16} />
                                                        </button>
                                                        <button type="button" className="tftActionBtn"
                                                            onClick={() => openDetails(d)} title="Détails"
                                                            style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}>
                                                            <Eye size={16} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button type="button" className="tftDetailsBtn"
                                                        onClick={() => openDetails(d)}>
                                                        Détails
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* ────── MODAL DÉTAILS ────── */}
            {showDetailsModal && selected && (
                <div className="tftModal">
                    <div className="tftModal__panel" role="dialog" aria-modal="true"
                        style={{ maxWidth: 680 }}>
                        <div className="tftModal__header">
                            <div className="tftModal__headLeft">
                                <div className="tftModal__icon">
                                    <BarChart2 size={18} />
                                </div>
                                <div>
                                    <div className="tftModal__title">
                                        Timesheet #{selected.id} — {selected.employeeName}
                                    </div>
                                    <div className="tftModal__sub">
                                        Soumis le {fmt(selected.dateEnvoi)}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tftIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tftModal__body">
                            <div className="tftGrid2">
                                {/* Collaborateur */}
                                <div className="tftPanel">
                                    <div className="tftPanel__title"><User size={16} /> Collaborateur</div>
                                    <div className="tftKv"><span>Matricule</span><b>{selected.employeeMatricule || "—"}</b></div>
                                    <div className="tftKv"><span>Nom</span><b>{selected.employeeName}</b></div>
                                </div>

                                {/* Période */}
                                <div className="tftPanel">
                                    <div className="tftPanel__title"><Calendar size={16} /> Période</div>
                                    <div className="tftKv"><span>Début</span><b>{fmt(selected.periodStart)}</b></div>
                                    <div className="tftKv"><span>Fin</span><b>{fmt(selected.periodEnd)}</b></div>
                                    <div className="tftKv"><span>Mode</span><b>{selected.mode || "—"}</b></div>
                                </div>

                                {/* Métriques */}
                                <div className="tftPanel tftPanel--full" style={{
                                    display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16,
                                }}>
                                    {[
                                        { label: "Heures saisies", val: `${selected.totalHeures ?? 0}h`, icon: <Clock size={18} />, color: "#3b82f6" },
                                        { label: "Heures supp.", val: `+${selected.heuresSupp ?? 0}h`, icon: <TrendingUp size={18} />, color: "#f59e0b" },
                                        { label: "Taux remplissage", val: `${selected.tauxRemplissage ?? 0}%`, icon: <BarChart2 size={18} />, color: tauxColor(selected.tauxRemplissage ?? 0) },
                                    ].map(m => (
                                        <div key={m.label} style={{
                                            background: "#f9fafb", borderRadius: 10, padding: "14px 16px",
                                            display: "flex", flexDirection: "column", gap: 6,
                                        }}>
                                            <div style={{ color: m.color }}>{m.icon}</div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: m.color }}>{m.val}</div>
                                            <div style={{ fontSize: 11, color: "#9ca3af" }}>{m.label}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Commentaire employé */}
                                {selected.commentaireEmploye && (
                                    <div className="tftPanel tftPanel--full">
                                        <div className="tftPanel__title"><FileText size={16} /> Commentaire employé</div>
                                        <div className="tftText">{selected.commentaireEmploye}</div>
                                    </div>
                                )}

                                {/* Décision manager si déjà prise */}
                                {selected.status !== "PENDING_MANAGER" && (
                                    <div className={`tftPanel tftPanel--full ${selected.status === "APPROVED_MANAGER" || selected.status === "APPROVED" ? "tftValidOk" : "tftValidNo"}`}>
                                        <div className="tftPanel__title">
                                            {selected.status === "APPROVED_MANAGER" || selected.status === "APPROVED"
                                                ? <><Check size={16} /> Validé par le manager</>
                                                : <><X size={16} /> Refusé par le manager</>}
                                        </div>
                                        {selected.managerComment && (
                                            <div className="tftRejectBox">
                                                <div className="tftRejectLabel">Commentaire</div>
                                                <div className="tftRejectText">{selected.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tftModal__footer">
                            {selected.status === "PENDING_MANAGER" ? (
                                <>
                                    <button className="tftBtn tftBtn--danger"
                                        onClick={() => { closeDetails(); openReject(selected); }} disabled={loading}>
                                        Refuser
                                    </button>
                                    <button className="tftBtn tftBtn--ok"
                                        onClick={() => { handleApprove(selected.id); closeDetails(); }} disabled={loading}>
                                        {loading ? <Loader size={16} className="spin" /> : "Valider"}
                                    </button>
                                </>
                            ) : null}
                            <button className="tftBtn tftBtn--ghost" onClick={closeDetails}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ────── MODAL REJET ────── */}
            {showRejectModal && selected && (
                <div className="tftModal">
                    <div className="tftModal__panel tftModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tftModal__header">
                            <div className="tftModal__headLeft">
                                <div className="tftModal__icon tftModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tftModal__title">Refuser la feuille de temps</div>
                                    <div className="tftModal__sub">{selected.employeeName} — {fmt(selected.periodStart)} → {fmt(selected.periodEnd)}</div>
                                </div>
                            </div>
                            <button type="button" className="tftIconBtn" onClick={closeReject}><X size={18} /></button>
                        </div>
                        <div className="tftModal__body">
                            <div className="tftMiniCard">
                                <div className="tftMiniRow">
                                    <span className="tftMuted">Heures saisies</span>
                                    <b>{selected.totalHeures ?? 0}h</b>
                                </div>
                                <div className="tftMiniRow">
                                    <span className="tftMuted">Taux remplissage</span>
                                    <b style={{ color: tauxColor(selected.tauxRemplissage ?? 0) }}>
                                        {selected.tauxRemplissage ?? 0}%
                                    </b>
                                </div>
                            </div>
                            <label className="tftLabel">Motif du refus <span className="tftReq">*</span></label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Expliquez pourquoi cette feuille de temps est refusée…"
                                className="tftTextarea"
                            />
                            <div className="tftHelp">Ce commentaire sera visible par le collaborateur</div>
                        </div>
                        <div className="tftModal__footer">
                            <button type="button" className="tftBtn tftBtn--ghost" onClick={closeReject}>Annuler</button>
                            <button type="button" className="tftBtn tftBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}>
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementFeuillesTempsManager;